import { Component, inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';
import { CustomFieldsModel, CustomFieldsType } from '@reducers/orm/custom-fields/custom-fields.model';
import { CustomFieldsService } from '@reducers/orm/custom-fields/custom-fields.service';
import { TimesheetModel } from '@reducers/orm/timesheet/timesheet.model';
import { TimesheetService } from '@reducers/orm/timesheet/timesheet.service';
import { TimesheetCustomFieldsColumns } from '@reducers/page-filters/page-filters.model';
import { convertArrayToObject } from '@reducers/shared/entity.helper';
import { useGridRowPinHold } from '@sb/ui/grid-scroll';
import isEqual from 'lodash-es/isEqual';
import { Subscription } from 'rxjs';
import { distinctUntilChanged, filter, switchMap, tap } from 'rxjs/operators';

@Component({
  standalone: false,
  selector: 'timesheet-row-custom-fields',
  templateUrl: './timesheet-row-custom-fields.component.html',
})
export class TimesheetRowCustomFieldsComponent implements OnInit, OnChanges, OnDestroy {
  private timesheetService = inject(TimesheetService);
  private customFieldsService = inject(CustomFieldsService);

  // Until the save settles: unmounting the row cancels the request.
  #rowHold = useGridRowPinHold();

  @Input()
  timesheet: TimesheetModel;
  @Input()
  customFields: CustomFieldsModel[] = [];
  @Input()
  canEdit: boolean;
  @Input()
  columns: TimesheetCustomFieldsColumns;

  form = new UntypedFormGroup({});
  mappedCustomFields: any[] = [];
  customFieldType = CustomFieldsType;
  private dataSubs = new Subscription();

  ngOnInit() {
    const mapping = this.customFieldsService.createCustomFieldMapping(this.customFields, this.timesheet.custom_fields, [
      CustomFieldsType.TEXT,
      CustomFieldsType.NUMERIC,
      CustomFieldsType.DECIMAL,
      CustomFieldsType.MONEY,
      CustomFieldsType.MULTI_SELECT,
    ]);
    this.mappedCustomFields = mapping.mappedCustomFields;
    this.form.addControl('customField', mapping.formArray);
    this.listenForChanges();
  }

  private listenForChanges() {
    this.dataSubs.add(
      this.form.valueChanges
        .pipe(
          filter(() => this.form.valid && this.canEdit),
          distinctUntilChanged((prev, current) => {
            if (!prev || !current) {
              return false;
            }

            return isEqual(prev, current);
          }),
          tap(() => this.#rowHold.hold()),
          switchMap(() => this.saveTimesheet()),
        )
        .subscribe({
          next: () => this.#rowHold.release(),
          error: () => this.#rowHold.release(),
        }),
    );
  }

  ngOnDestroy(): void {
    this.dataSubs.unsubscribe();
  }

  onCustomFieldChange(value: string, form: AbstractControl) {
    form.get('value').setValue(value);
  }

  private saveTimesheet() {
    if (!this.form.valid) {
      return;
    }

    const timesheetToUpdate = {
      id: this.timesheet.id,
      custom_fields: this.customFieldsService.mapFormDataToRequestData(this.form.controls['customField']),
    };

    return this.timesheetService.save(timesheetToUpdate);
  }

  ngOnChanges(changes: SimpleChanges): void {
    const changed = !isEqual(
      changes.timesheet?.currentValue?.custom_fields,
      changes.timesheet?.previousValue?.custom_fields,
    );
    if (!changed) {
      return;
    }
    if (!changes.timesheet || !this.form.contains('customField')) {
      return;
    }

    const newCustomFieldEntities = convertArrayToObject(
      'id',
      Object.entries(this.timesheet.custom_fields).map(([id, value]) => ({ id, value })),
    );
    const customFieldValues: { id: string }[] | undefined = this.form.controls['customField']?.value;
    const updatedCustomFields = customFieldValues
      ?.filter((customField) => newCustomFieldEntities[customField.id])
      .map((customField) => ({
        id: customField.id,
        value: newCustomFieldEntities[customField.id].value,
      }));

    this.form.patchValue(
      {
        customField: updatedCustomFields,
      },
      {
        emitEvent: false,
      },
    );
  }
}
